// src/context/AuthContext.jsx
"use client";
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
} from "react";
import axios from "axios";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // Initialize state synchronously from localStorage to prevent flash
  const [user, setUser] = useState(() => {
    try {
      const rawUser = localStorage.getItem("bookatherapy_user");
      return rawUser ? JSON.parse(rawUser) : null;
    } catch {
      localStorage.removeItem("bookatherapy_user");
      return null;
    }
  });

  const [token, setToken] = useState(() => {
    const rawToken = localStorage.getItem("bookatherapy_token");
    if (rawToken) {
      axios.defaults.headers.common.Authorization = `Bearer ${rawToken}`;
    }
    return rawToken || null;
  });

  const [isLoading, setIsLoading] = useState(false);

  // No longer need useEffect for initial load since we do it synchronously above

  const login = (userData, jwt) => {
    if (jwt) {
      localStorage.setItem("bookatherapy_token", jwt);
      setToken(jwt);
      axios.defaults.headers.common.Authorization = `Bearer ${jwt}`;
    }
    localStorage.setItem("bookatherapy_user", JSON.stringify(userData));
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem("bookatherapy_user");
    localStorage.removeItem("bookatherapy_token");
    setUser(null);
    setToken(null);
    delete axios.defaults.headers.common.Authorization;
  };

  const value = useMemo(
    () => ({
      user,
      token,
      login,
      logout,
      isAuthenticated: !!user && !!token,
      isLoading,
    }),
    [user, token, isLoading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
